import React, {
    useEffect,
    useMemo,
    useState,
  } from "react";
  
  import {
    collection,
    getDocs,
    query,
    where,
  } from "firebase/firestore";
  
  import { db } from "../firebase";
  
  import "./UserCommissionRecord.css";
  
  
  /* =========================================================
     COLLECTION
  ========================================================= */
  
  const COMMISSION_COLLECTION =
    "admin_commissions";
  
  
  /* =========================================================
     FORMAT MONEY
  ========================================================= */
  
  const formatMoney = (value) => {
    return Number(value || 0).toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );
  };
  
  
  /* =========================================================
     FORMAT CURRENCY OBJECT
  ========================================================= */
  
  const formatCurrency = (object = {}) => {
    const entries =
      Object.entries(object).filter(
        ([, value]) =>
          Number(value || 0) > 0
      );
  
    if (!entries.length) {
      return "0.00";
    }
  
    return entries
      .map(
        ([currency, value]) =>
          `${formatMoney(value)} ${currency}`
      )
      .join(" + ");
  };
  
  
  /* =========================================================
     PARSE FIRESTORE DATE
  ========================================================= */
  
  const parseFirestoreDate = (value) => {
    if (!value) {
      return null;
    }
  
    if (
      typeof value?.toDate === "function"
    ) {
      return value.toDate();
    }
  
    if (
      typeof value?.seconds === "number"
    ) {
      return new Date(
        value.seconds * 1000
      );
    }
  
    if (value instanceof Date) {
      return value;
    }
  
    if (typeof value === "number") {
      return new Date(
        value > 100000000000
          ? value
          : value * 1000
      );
    }
  
    const date = new Date(value);
  
    return Number.isNaN(
      date.getTime()
    )
      ? null
      : date;
  };
  
  
  /* =========================================================
     FORMAT DATE
  ========================================================= */
  
  const formatDate = (value) => {
    const date =
      parseFirestoreDate(value);
  
    if (!date) {
      return "-";
    }
  
    return date.toLocaleString(
      undefined,
      {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }
    );
  };
  
  
  /* =========================================================
     STATUS CLASS
  ========================================================= */
  
  const statusClass = (status) => {
    if (status === "success") {
      return "commission-success";
    }
  
    if (status === "processing") {
      return "commission-processing";
    }
  
    return "commission-waiting";
  };
  
  
  /* =========================================================
     COMPONENT
  ========================================================= */
  
  const UserCommissionRecord = () => {
  
    const [
      username,
      setUsername,
    ] = useState("");
  
    const [
      records,
      setRecords,
    ] = useState([]);
  
    const [
      loading,
      setLoading,
    ] = useState(true);
  
    const [
      error,
      setError,
    ] = useState("");
  
    const [
      selectedRecord,
      setSelectedRecord,
    ] = useState(null);
  
  
    /* =======================================================
       GET USERNAME
    ======================================================= */
  
    useEffect(() => {
      const savedUsername =
        localStorage.getItem(
          "username"
        ) || "";
  
      setUsername(
        savedUsername.trim()
      );
    }, []);
  
  
    /* =======================================================
       LOAD COMMISSIONS
    ======================================================= */
  
    useEffect(() => {
  
      const loadCommissions =
        async () => {
  
          if (!username) {
            setRecords([]);
            setLoading(false);
            return;
          }
  
          setLoading(true);
          setError("");
  
          try {
  
            const commissionQuery =
              query(
                collection(
                  db,
                  COMMISSION_COLLECTION
                ),
  
                where(
                  "username",
                  "==",
                  username
                )
              );
  
            const snapshot =
              await getDocs(
                commissionQuery
              );
  
            const data = [];
  
            snapshot.forEach(
              (item) => {
  
                data.push({
                  id: item.id,
                  ...item.data(),
                });
  
              }
            );
  
  
            /* =============================================
               NEWEST FIRST
            ============================================= */
  
            data.sort(
              (a, b) => {
  
                const dateA =
                  parseFirestoreDate(
                    a.paidAt ||
                    a.updatedAt ||
                    a.createdAt
                  )?.getTime() || 0;
  
                const dateB =
                  parseFirestoreDate(
                    b.paidAt ||
                    b.updatedAt ||
                    b.createdAt
                  )?.getTime() || 0;
  
                return dateB - dateA;
              }
            );
  
            setRecords(data);
  
          } catch (err) {
  
            console.error(
              "User commission error:",
              err
            );
  
            setError(
              err?.message ||
              "Imeshindikana kupakia commission."
            );
  
          } finally {
  
            setLoading(false);
  
          }
  
        };
  
  
      loadCommissions();
  
    }, [username]);
  
  
    /* =======================================================
       TOTAL COMMISSION
    ======================================================= */
  
    const totalCommission =
      useMemo(() => {
  
        const result = {};
  
        records.forEach(
          (record) => {
  
            const amount =
              record.editedTotal ||
              record.total ||
              {};
  
            Object.entries(
              amount
            ).forEach(
              ([currency, value]) => {
  
                result[currency] =
                  (
                    result[currency] ||
                    0
                  ) +
                  Number(value || 0);
  
              }
            );
  
          }
        );
  
        return result;
  
      }, [records]);
  
  
    /* =======================================================
       BACK BUTTON
    ======================================================= */
  
    const handleBack = () => {
  
      if (
        window.history.length > 1
      ) {
        window.history.back();
      }
  
    };
  
  
    /* =======================================================
       LOADING
    ======================================================= */
  
    if (loading) {
  
      return (
        <div className="user-commission-page">
  
          <div className="commission-loading">
  
            <div className="commission-spinner" />
  
            <p>
              Loading your commissions...
            </p>
  
          </div>
  
        </div>
      );
  
    }
  
  
    /* =======================================================
       NO USERNAME
    ======================================================= */
  
    if (!username) {
  
      return (
        <div className="user-commission-page">
  
          <button
            type="button"
            className="commission-corner-back"
            onClick={handleBack}
          >
            <span className="back-icon">
              ←
            </span>
  
            <span>
              Back
            </span>
          </button>
  
  
          <div className="commission-empty">
  
            <h3>
              Username haijapatikana
            </h3>
  
            <p>
              Tafadhali login kwanza.
            </p>
  
          </div>
  
        </div>
      );
  
    }
  
  
    /* =======================================================
       UI
    ======================================================= */
  
    return (
  
      <div className="user-commission-page">
  
  
        {/* =================================================
            BACK BUTTON - TOP LEFT CORNER
        ================================================= */}
  
        <button
          type="button"
          className="commission-corner-back"
          onClick={handleBack}
        >
  
          <span className="back-icon">
            ←
          </span>
  
          <span>
            Back
          </span>
  
        </button>
  
  
        <div className="user-commission-container">
  
  
          {/* =================================================
              HEADER
          ================================================= */}
  
          <div className="commission-header">
  
            <div>
  
              <span>
                MY COMMISSION
              </span>
  
              <h1>
                Commission Record
              </h1>
  
              <p>
                Hi {username}, hapa unaweza
                kuona commission zako.
              </p>
  
            </div>
  
  
            {/* =================================================
                USERNAME
            ================================================= */}
  
            <div className="commission-user">
  
              <span>
                USERNAME
              </span>
  
              <strong>
                {username}
              </strong>
  
            </div>
  
          </div>
  
  
          {/* =================================================
              ERROR
          ================================================= */}
  
          {error && (
  
            <div className="commission-error">
  
              <span>
                ⚠️
              </span>
  
              <span>
                {error}
              </span>
  
            </div>
  
          )}
  
  
          {/* =================================================
              TOTAL
          ================================================= */}
  
          <div className="commission-summary">
  
            <div>
  
              <span>
                TOTAL COMMISSION
              </span>
  
              <strong>
                {formatCurrency(
                  totalCommission
                )}
              </strong>
  
            </div>
  
  
            <div>
  
              <span>
                RECORDS
              </span>
  
              <strong>
                {records.length}
              </strong>
  
            </div>
  
          </div>
  
  
          {/* =================================================
              RECORDS
          ================================================= */}
  
          <div className="commission-record-section">
  
            <div className="section-heading">
  
              <div>
  
                <span>
                  HISTORY
                </span>
  
                <h2>
                  Commission Records
                </h2>
  
              </div>
  
            </div>
  
  
            {records.length === 0 ? (
  
              <div className="commission-empty">
  
                <div>
                  💰
                </div>
  
                <h3>
                  No Commission Records
                </h3>
  
                <p>
                  Bado hujapata commission yoyote.
                </p>
  
              </div>
  
            ) : (
  
              <div className="commission-table-wrapper">
  
                <table className="commission-table">
  
                  <thead>
  
                    <tr>
  
                      <th>
                        DATE
                      </th>
  
                      <th>
                        AMOUNT
                      </th>
  
                      <th>
                        STATUS
                      </th>
  
                      <th>
                        DETAILS
                      </th>
  
                    </tr>
  
                  </thead>
  
  
                  <tbody>
  
                    {records.map(
                      (record) => {
  
                        const amount =
                          record.editedTotal ||
                          record.total ||
                          {};
  
                        return (
  
                          <tr
                            key={
                              record.id
                            }
                          >
  
                            {/* DATE */}
  
                            <td>
  
                              <div className="record-date">
  
                                <strong>
                                  {formatDate(
                                    record.paidAt ||
                                    record.createdAt
                                  )}
                                </strong>
  
                              </div>
  
                            </td>
  
  
                            {/* AMOUNT */}
  
                            <td>
  
                              <strong
                                className="record-amount"
                              >
                                {formatCurrency(
                                  amount
                                )}
                              </strong>
  
                            </td>
  
  
                            {/* STATUS */}
  
                            <td>
  
                              <span
                                className={
                                  `record-status ` +
                                  statusClass(
                                    record.status
                                  )
                                }
                              >
  
                                <i />
  
                                {record.status ||
                                  "waiting"}
  
                              </span>
  
                            </td>
  
  
                            {/* DETAILS */}
  
                            <td>
  
                              <button
                                type="button"
                                className="details-button"
                                onClick={() =>
                                  setSelectedRecord(
                                    record
                                  )
                                }
                              >
  
                                <span>
                                  👁️
                                </span>
  
                                <span>
                                  Details←
                                </span>
  
                              </button>
  
                            </td>
  
                          </tr>
  
                        );
  
                      }
                    )}
  
                  </tbody>
  
                </table>
  
              </div>
  
            )}
  
          </div>
  
        </div>
  
  
        {/* ===================================================
            DETAILS MODAL
        =================================================== */}
  
        {selectedRecord && (
  
          <div
            className="commission-modal-overlay"
            onClick={() =>
              setSelectedRecord(null)
            }
          >
  
            <div
              className="commission-modal"
              onClick={(event) =>
                event.stopPropagation()
              }
            >
  
  
              {/* MODAL HEADER */}
  
              <div className="modal-header">
  
                <div>
  
                  <span>
                    COMMISSION DETAILS
                  </span>
  
                  <h2>
                    {selectedRecord.username}
                  </h2>
  
                </div>
  
  
                <button
                  type="button"
                  className="modal-close"
                  onClick={() =>
                    setSelectedRecord(null)
                  }
                >
                  ×
                </button>
  
              </div>
  
  
              {/* DATE */}
  
              <div className="modal-date">
  
                <span>
                  DATE & TIME
                </span>
  
                <strong>
                  {formatDate(
                    selectedRecord.paidAt ||
                    selectedRecord.createdAt
                  )}
                </strong>
  
              </div>
  
  
              {/* STATUS */}
  
              <div className="modal-status-row">
  
                <span>
                  STATUS
                </span>
  
                <span
                  className={
                    `record-status ` +
                    statusClass(
                      selectedRecord.status
                    )
                  }
                >
  
                  <i />
  
                  {selectedRecord.status ||
                    "waiting"}
  
                </span>
  
              </div>
  
  
              {/* LEVEL DETAILS */}
  
              <div className="level-details">
  
  
                {/* LEVEL 1 */}
  
                <div className="detail-level level1-detail">
  
                  <div>
  
                    <span>
                      LEVEL 1
                    </span>
  
                    <small>
                      8%
                    </small>
  
                  </div>
  
                  <strong>
                    {formatCurrency(
                      selectedRecord.level1
                    )}
                  </strong>
  
                </div>
  
  
                {/* LEVEL 2 */}
  
                <div className="detail-level level2-detail">
  
                  <div>
  
                    <span>
                      LEVEL 2
                    </span>
  
                    <small>
                      6%
                    </small>
  
                  </div>
  
                  <strong>
                    {formatCurrency(
                      selectedRecord.level2
                    )}
                  </strong>
  
                </div>
  
  
                {/* LEVEL 3 */}
  
                <div className="detail-level level3-detail">
  
                  <div>
  
                    <span>
                      LEVEL 3
                    </span>
  
                    <small>
                      4%
                    </small>
  
                  </div>
  
                  <strong>
                    {formatCurrency(
                      selectedRecord.level3
                    )}
                  </strong>
  
                </div>
  
              </div>
  
  
              {/* TOTAL */}
  
              <div className="modal-total">
  
                <div>
  
                  <span>
                    TOTAL COMMISSION
                  </span>
  
                  <strong>
                    {formatCurrency(
                      selectedRecord.total
                    )}
                  </strong>
  
                </div>
  
  
                <div>
  
                  <span>
                    PAYMENT AMOUNT
                  </span>
  
                  <strong>
                    {formatCurrency(
                      selectedRecord.editedTotal ||
                      selectedRecord.total
                    )}
                  </strong>
  
                </div>
  
              </div>
  
  
              {/* PAID AMOUNT */}
  
              {selectedRecord.previousPaid &&
                Object.keys(
                  selectedRecord.previousPaid
                ).length > 0 && (
  
                  <div className="modal-paid">
  
                    <span>
                      PAID AMOUNT
                    </span>
  
                    <strong>
                      {formatCurrency(
                        selectedRecord.previousPaid
                      )}
                    </strong>
  
                  </div>
  
                )}
  
            </div>
  
          </div>
  
        )}
  
      </div>
  
    );
  };
  
  
  export default UserCommissionRecord;